import { DeepPartial, ILabel, isObject, toCamelCase } from '@websolutespa/bom-core';
import { useLabel } from '@websolutespa/bom-mixer-hooks';
import { ChangeEvent, createContext, useContext, useRef } from 'react';
import { toast } from 'sonner';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { LLM_BLOCKS, LlmBlocks } from '../llm/blocks';
import { LLM_COMPONENTS, LlmComponents } from '../llm/components';
import { getThemes } from '../llm/theme';
import { createVars, getVars } from '../llm/vars';
import { LLM_DEFAULT_MAX_FILE_SIZE, LLM_DEFAULT_MIME_TYPES, LlmApp, LlmChunk, LlmChunkAction, LlmChunkActionItem, LlmChunkHeader, LlmChunkItem, LlmChunkString, LlmChunkText, LlmDecodedMessage, LlmHistory, LlmInstance, LlmItem, LlmRecognitionMode, LlmStorageMode, LlmStoredHistory, LlmSynthesisMode, LlmTest, LlmTheme, LlmVars, StreamResponse } from '../types';
import { ApiService } from './api.service';
import { pushDataLayer } from './gtm.service';
import { MessageDecoder, MessageService } from './message.service';
import { getMock } from './mock';
import { chunksToTexts, Speech } from './speech';
import { fileToResizedBase64, formatBytes } from './upload';
import { LlmViewOptions, LlmViewProvider } from './useLlmView';

export type LlmDecorateUrl = (item: LlmItem) => (string | void) | Promise<string | void>;

export type LlmOnAction = (item: LlmChunkAction | LlmChunkActionItem) => (boolean | void) | Promise<boolean | void>;

export type LlmOnMessage = (message: StreamResponse) => (void) | Promise<void>;

export type LlmSystemContext = Record<string, string>;

export type LlmContext = {
  title: string;
  context: LlmSystemContext;
};

export type LlmPlugin = (options: LlmOptions) => LlmOptions;

export type LlmOnInit = (state: LlmState) => void;

export type LlmHistoryOptions = {
  get?: () => Promise<LlmStoredHistory[]>;
  addItem?: (item: LlmStoredHistory) => Promise<void>;
  removeItem?: (item: { threadId: string, messageId?: string }) => Promise<void>;
};

export type LlmOptions = {
  apiKey: string;
  appKey: string;
  blocks?: Partial<LlmBlocks>;
  components?: Partial<LlmComponents>;
  contexts?: LlmContext[];
  customTheme?: DeepPartial<LlmTheme>;
  defaultTheme?: DeepPartial<LlmTheme>;
  endpoint?: string;
  history?: LlmHistoryOptions;
  plugins?: LlmPlugin[];
  pluginLabels?: ILabel[];
  storageMode?: LlmStorageMode;
  systemContext?: LlmSystemContext;
  test?: LlmTest;
  decorateUrl?: LlmDecorateUrl;
  onAction?: LlmOnAction;
  onMessage?: LlmOnMessage;
  onInit?: LlmOnInit;
} & LlmViewOptions;

export type LlmUpload = {
  file: File;
  base64?: string;
};

export type LlmUploaded = {
  id: string;
  name: string;
  filename: string;
  mimeType: string;
  filesize: number;
  createdAt: string;
  updatedAt: string;
  url: string;
  type: 'image' | 'file';
};

export type LlmAttachment = {
  type: 'image' | 'file';
  url: string;
};

export type LlmProps = {
  app?: LlmApp;
  asideEnabled: boolean;
  blocks: LlmBlocks;
  chunks?: LlmChunkItem[];
  components: LlmComponents;
  contexts?: LlmContext[];
  currentContext?: LlmContext;
  embed?: Element | undefined;
  history: LlmHistory[];
  hydrated: boolean;
  instance?: LlmInstance;
  locale: string;
  maxFileSize: number;
  message?: LlmDecodedMessage;
  messages: LlmDecodedMessage[];
  mimeTypes: string;
  prompt: string;
  ready: boolean;
  recognitionMode?: LlmRecognitionMode;
  speakEnabled: boolean;
  storageMode: LlmStorageMode;
  storedHistory: LlmStoredHistory[];
  streaming: boolean;
  synthesisMode?: LlmSynthesisMode;
  systemContext: LlmSystemContext;
  theme: LlmTheme;
  threadId?: string;
  upload?: LlmUpload;
  vars: LlmVars;
};

export type LlmActions = {
  abort: () => void;
  addFile: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  clear: () => void;
  decorateUrl: (item: LlmItem, url?: string) => Promise<string>;
  formRecap: (error?: unknown) => void;
  formRequest: (choice: boolean) => void;
  getApi: () => ApiService;
  hasSpeechRecognitionSupport: () => boolean;
  hasSpeechSynthesisSupport: () => boolean;
  hasUploadSupport: () => boolean;
  init: (locale: string, instance: LlmInstance) => Promise<void>;
  onAction: (item: LlmChunkAction | LlmChunkActionItem) => Promise<boolean | void>;
  onMessage: (message: StreamResponse) => Promise<void>;
  recognizeStart: () => void;
  recognizeStop: () => void;
  removeFile: () => void;
  removeHistory: (history: LlmHistory) => void;
  send: (message: string, onPrompt?: () => void, onMessage?: (response: StreamResponse) => void, onEnd?: (response: StreamResponse) => void) => Promise<void>;
  setCurrentContext: (currentContext: LlmContext) => void;
  setEmbed: (element: Element, active: boolean) => void;
  setHistory: (history: LlmHistory) => void;
  setPrompt: (prompt: string) => void;
  setVars: (vars: Partial<LlmVars>) => void;
  toggleAside: () => void;
  toggleSpeak: () => void;
};

export type LlmState = LlmProps & {
  actions: LlmActions;
};

export type LlmConfig = LlmOptions & {
  label: (key: string) => string
};

export type LlmStore = ReturnType<typeof createLlmStore>;

const createLlmStore = (config: LlmConfig) => {

  const sanitizedConfig = (config.plugins || []).reduce<LlmConfig>((p: LlmConfig, c) => {
    return c(p) as LlmConfig;
  }, {
    ...config,
    /**
     * test remote api
    ...({
      appKey: 'xxxxxxxxxxxxxx',
      apiKey: 'xxxxxxxxxxxxxx',
      threadId: 'xxxxxxxxxxxxxx',
      endpoint: 'https://platform.websolute.ai',
      endpoint: 'https://platform-ai-dev.ws-deploy-01.wslabs.it',
      endpoint: 'http://localhost:4000/bowl',
      test: false,
    }),
     */
  });

  const {
    apiKey,
    appKey,
    contexts,
    customTheme,
    endpoint,
    preview,
    test,
    threadId,
    decorateUrl,
    label,
    onAction,
    onMessage,
    ...options
  } = sanitizedConfig;

  const theme = getThemes([customTheme], config.defaultTheme);
  const vars = getVars(theme);

  const props: LlmProps = {
    asideEnabled: false,
    blocks: {
      ...LLM_BLOCKS,
      ...options.blocks,
    },
    components: {
      ...LLM_COMPONENTS,
      ...options.components,
    },
    contexts,
    history: [],
    hydrated: false,
    locale: 'en',
    maxFileSize: LLM_DEFAULT_MAX_FILE_SIZE,
    messages: [],
    mimeTypes: LLM_DEFAULT_MIME_TYPES,
    prompt: '',
    ready: false,
    speakEnabled: true,
    storageMode: options.storageMode || 'local',
    storedHistory: [],
    streaming: false,
    systemContext: {
      ...options.systemContext,
    },
    theme,
    vars,
  };

  let messageService: MessageService | undefined = undefined;

  const speech = new Speech();

  const storeKey = toCamelCase(`llm-${appKey}`);

  let getHistory = async (storedHistory: LlmStoredHistory[] = []) => {
    if (
      typeof options.history === 'object' &&
      typeof options.history.get === 'function'
    ) {
      try {
        storedHistory = await options.history.get();
      } catch (error) {
        console.log('useLlm.getHistory.error', error);
      }
    }
    return storedHistory;
  };

  let addItemToHistory = async (item: LlmStoredHistory) => {
    if (
      typeof options.history === 'object' &&
      typeof options.history.addItem === 'function'
    ) {
      try {
        await options.history.addItem(item);
      } catch (error) {
        console.log('useLlm.addItemToHistory.error', error);
      }
    }
  };

  let removeItemFromHistory = async (item: { threadId: string, messageId?: string }) => {
    if (
      typeof options.history === 'object' &&
      typeof options.history.removeItem === 'function'
    ) {
      try {
        await options.history.removeItem(item);
      } catch (error) {
        console.log('useLlm.removeItemFromHistory.error', error);
      }
    }
  };

  const useStore = createStore<LlmState>()(
    persist(
      (set, get) => ({
        ...props,
        actions: {
          getApi: () => {
            const state = get();
            const { locale, threadId } = state;
            const apiService = new ApiService({
              apiKey,
              appKey,
              threadId,
              endpoint,
              locale,
              test,
              preview,
            });
            return apiService;
          },
          init: async (locale: string, instance: LlmInstance) => {
            const state = get();
            const apiService = new ApiService({
              apiKey,
              appKey,
              threadId: threadId || state.threadId,
              endpoint,
              locale,
              test,
              userId: options.systemContext?.userId || appKey,
              preview,
            });
            const app = await apiService.getInfo();
            if (!app) {
              return;
            }
            const textToSpeechApiKey = app.contents.textToSpeechApiKey;
            const textToSpeechVoiceId = app.contents.textToSpeechVoiceId;
            const synthesisMode = app.contents.disableSpeechSynthesis ? 'none' :
              (app.contents.synthesisMode || 'default');
            const recognitionMode = app.contents.disableSpeechRecognition ? 'none' :
              (app.contents.recognitionMode || 'default');
            const speechEnabled = state.speakEnabled;
            speech.lang = locale;
            speech.enabled = speechEnabled;
            speech.textToSpeechApiKey = textToSpeechApiKey;
            speech.textToSpeechVoiceId = textToSpeechVoiceId;
            speech.synthesisMode = synthesisMode;
            speech.recognitionMode = recognitionMode;
            // console.log('useLlm.init', { textToSpeechApiKey, textToSpeechVoiceId, synthesisMode, recognitionMode, speechEnabled });
            speech.on('result', async (transcript: string) => {
              const state = get();
              state.actions.setPrompt(transcript);
            });
            if (app.contents.enableHistory && !options.history) {
              getHistory = apiService.getHistory.bind(apiService);
              addItemToHistory = apiService.addItemToHistory.bind(apiService);
              removeItemFromHistory = apiService.removeItemFromHistory.bind(apiService);
            }
            const thread = MessageDecoder.decodeThread(app.thread);
            const testTheme = test ? getMock(locale, test).app?.contents?.customTheme : {};
            /*
            console.log(
              'appTheme', app.contents.customTheme,
              'testTheme', testTheme,
              'customTheme', customTheme
            );
            */
            const theme = getThemes([app.contents.customTheme, testTheme, customTheme], config.defaultTheme);
            const mode = theme.mode;
            const storageMode = options.storageMode || app.contents.storageMode || 'local';
            const storedHistory = await getHistory(state.storedHistory);
            // console.log('useLlm.storedHistory', storedHistory);
            const history = await apiService.decorateHistory(storedHistory, mode);
            // console.log('useLlm.history', history);
            /*
            // !!! session restored via threadId storage merge
            if (storageMode === 'session') {
              // console.log('storageMode', storageMode, history);
              if (history.length > 0) {
                thread = {
                  threadId: history[0].threadId,
                  messages: history[0].messages,
                };
              }
            }
            */
            /*
            console.log(
              'theme', theme
            );
            */
            const vars = getVars(theme);
            createVars(vars);
            // console.log('thread', thread);
            const patchState: Partial<LlmState> = {
              app,
              history,
              instance,
              locale,
              ready: true,
              recognitionMode,
              storageMode,
              storedHistory,
              synthesisMode,
              theme,
              threadId,
              vars,
              mimeTypes: app.contents.mimeTypes || LLM_DEFAULT_MIME_TYPES,
              maxFileSize: app.contents.maxFileSize || LLM_DEFAULT_MAX_FILE_SIZE,
              ...thread,
            };

            const newState = {
              ...state,
              ...patchState,
            };

            if (typeof sanitizedConfig.onInit === 'function') {
              sanitizedConfig.onInit(newState);
            }

            set(() => (patchState));
          },
          send: async (prompt, onPrompt, onMessage, onEnd) => {
            speech.cancel();
            const state = get();
            messageService = new MessageService({
              apiKey,
              appKey,
              endpoint,
              locale: state.locale,
              test,
            });
            let uploaded: LlmUploaded | undefined;
            if (state.upload) {
              // https://platform-ai-dev.ws-deploy-01.wslabs.it/api/llmApp/68ac0bfed671c7c791c6b8ec?locale=it&draft=false&depth=1
              const apiService = new ApiService({
                apiKey,
                appKey,
                endpoint,
                locale: state.locale,
                preview,
                test,
                threadId,
              });
              uploaded = await apiService.upload(state.upload);
              if (!uploaded) {
                return;
              }
            }
            pushDataLayer({
              event: (state.messages.length > 0 ? 'llm_message' : 'llm_start'),
            });
            const messages = await messageService.addUserMessage(state.messages, prompt, uploaded);
            set(state => ({
              chunks: [],
              message: undefined,
              messages,
              prompt: '',
              streaming: true,
              upload: undefined,
            }));
            if (typeof onPrompt === 'function') {
              onPrompt();
            }
            const request = await messageService.getRequest(messages, state);
            await messageService.sendMessage(request,
              // onMessage
              (response: StreamResponse) => {
                // console.log('messageService.sendMessage.onMessage', response.chunks);
                set(state => ({
                  chunks: response.chunks,
                }));
                if (typeof onMessage === 'function') {
                  onMessage(response);
                }
                state.actions.onMessage(response);
              },
              // onEnd
              (response: StreamResponse) => {
                // console.log('messageService.sendMessage.onEnd', response);
                const chunks = [...response.chunks];
                const assistantMessage: LlmDecodedMessage = {
                  chunks,
                  content: '',
                  createdAt: new Date().toISOString(),
                  messageId: response.messageId,
                  role: 'assistant',
                  threadId: response.threadId,
                };
                const responseMessages = [...messages, assistantMessage];
                // 15/12/2025 disabling form request feature, todo add payload flag
                /*
                if (messageService && messageService.shouldAddFormRequest(responseMessages)) {
                  chunks.push({
                    type: 'formRequest',
                  });
                }
                */
                // filtering history
                const validChunks = chunks.filter(x =>
                  typeof x.type === 'string' &&
                  !['header', 'info', 'end', 'formRequest', 'formRecap', 'formRecapSuccess', 'formRecapError'].includes(x.type)
                );
                const parsedChunks: LlmChunkItem[] = [];
                validChunks.reduce((p, c) => {
                  const firstChunk = p.length > 1 && p[p.length - 2];
                  const secondChunk = p.length > 1 && p[p.length - 1];
                  if (
                    firstChunk &&
                    secondChunk &&
                    c.type === 'string' &&
                    firstChunk.type === 'string' &&
                    secondChunk.type === 'string'
                  ) {
                    (secondChunk as LlmChunkString).content += c.content;
                  } else {
                    p.push({ ...c });
                  }
                  return p;
                }, parsedChunks);
                assistantMessage.content = parsedChunks.map(x => JSON.stringify(x)).join(',') + ',';
                // console.log('messageService.sendMessage.onEnd', assistantMessage);
                const history = messagesToHistory(responseMessages);
                const historyItem = history[0]!;
                addItemToHistory({
                  threadId: historyItem.threadId,
                  messageId: historyItem.messageId,
                });
                let newHistory = [historyItem, ...state.history];
                if (theme.mode !== 'page') {
                  newHistory = newHistory.reduce<LlmHistory[]>((p, c) => {
                    if (p.findIndex(x => x.threadId === c.threadId) === -1) {
                      p.push(c);
                    }
                    return p;
                  }, []);
                }
                set(state => ({
                  chunks: undefined,
                  history: newHistory,
                  message: assistantMessage,
                  messages: responseMessages,
                  streaming: false,
                  threadId: response.threadId,
                }));
                speech.reader.end();
                if (typeof onEnd === 'function') {
                  onEnd(response);
                }
              },
              // onChunk
              (chunks: LlmChunk[]) => {
                // console.log('onChunk', chunks);
                const header = chunks.find(x => isObject(x) && x.type === 'header');
                if (header) {
                  set(state => ({
                    threadId: (header as LlmChunkHeader).threadId,
                  }));
                  // console.log('onChunk', header);
                }
                const texts = chunksToTexts(chunks);
                texts.forEach(text => speech.reader.add(text));
              });
          },
          abort: () => {
            speech.cancel();
            if (messageService) {
              messageService.abort();
              messageService = undefined;
            }
          },
          setCurrentContext: (currentContext) => {
            const { systemContext } = get();
            set(state => ({
              currentContext,
              systemContext: {
                ...systemContext,
                ...currentContext.context,
              },
            }));
          },
          setVars: (vars) => set(state => ({ vars: Object.assign({}, state.vars, vars) })),
          setEmbed: (element: Element, active: boolean) => {
            const embed = get().embed;
            if (!active && embed !== element) {
              return;
            }
            set(state => {
              return { embed: active ? element : undefined };
            });
          },
          setPrompt: (prompt) => {
            const promptTokenLimit = get().app?.contents.promptTokenLimit || 8000;
            const promptWordsLimit = promptTokenLimit / 2;
            let words = 0,
              lastCharIsNonChar = false;
            const sanitizedPrompt = prompt.split('').reduce((p, c) => {
              const isNonChar = Boolean(c.match(/\W/));
              if (isNonChar && !lastCharIsNonChar) {
                words++;
              }
              lastCharIsNonChar = isNonChar;
              if (words < promptWordsLimit) {
                return p + c;
              }
              return p;
            }, '');
            if (sanitizedPrompt.length < prompt.length) {
              toast('Prompt token limit exceeded!');
            }
            set(state => ({ prompt: sanitizedPrompt }));
          },
          hasSpeechSynthesisSupport: () => {
            return speech.hasTextToSpeechSupport;
          },
          hasSpeechRecognitionSupport: () => {
            return speech.hasSpeechToTextSupport;
          },
          toggleSpeak: () => {
            set(state => {
              const speakEnabled = !state.speakEnabled;
              speech.setEnabled(speakEnabled);
              return { speakEnabled };
            });
          },
          toggleAside: () => {
            set(state => {
              const asideEnabled = !state.asideEnabled;
              return { asideEnabled };
            });
          },
          recognizeStart: () => {
            const state = get();
            speech.recognizeStart();
          },
          recognizeStop: () => {
            const state = get();
            speech.recognizeStop();
          },
          hasUploadSupport: () => {
            const state = get();
            return state.app?.contents.enableUpload === true;
          },
          addFile: async (event: ChangeEvent<HTMLInputElement>) => {
            const { maxFileSize } = get();
            const files = event.target?.files;
            // console.log('useLlm.addFile', files, Array.isArray(files));
            try {
              if (files && files.length > 0) {
                const file = files[0] as File;
                if (!file) {
                  console.log('useLlm.addFile.error', 'file not found');
                  return;
                }
                // const app = get().app;
                if (file.size > maxFileSize) {
                  toast(`Max file size exceeded ${formatBytes(maxFileSize)}`);
                  return;
                }
                const base64 = file.type.indexOf('image') === 0 ? await fileToResizedBase64(file) : undefined;
                (event.target.value as any) = null;
                // console.log('useLlm.addFile.base64', base64);
                const upload = {
                  file,
                  base64,
                };
                // console.log('useLlm.addFile.success', upload);
                return set({ upload });
              } else {
                console.log('useLlm.addFile.error', 'file not found');
              }
            } catch (error) {
              console.log('useLlm.addFile.error', 'cannot parse file');
            }
          },
          removeFile: () => {
            return set({ upload: undefined });
          },
          formRequest: (choice) => {
            const choiceMessage = choice ? label('llm.formRequestYes') : label('llm.formRequestNo');
            const userMessage = {
              chunks: [{
                type: 'string' as const,
                content: choiceMessage,
              }],
              content: choiceMessage,
              role: 'user',
            };
            const messages: LlmDecodedMessage[] = [userMessage];
            if (choice) {
              const assistantMessage = {
                chunks: [{
                  type: 'formRecap' as const,
                }],
                content: '',
                role: 'assistant',
                createdAt: new Date().toISOString(),
              };
              messages.push(assistantMessage);
            }
            set(state => ({
              messages: [...state.messages, ...messages],
            }));
          },
          formRecap: (error) => {
            if (error) {
              console.warn('useLlm.formRecap', error);
            }
            const messages: LlmDecodedMessage[] = [];
            const assistantMessage = {
              chunks: [{
                type: error ? 'formRecapError' as const : 'formRecapSuccess' as const,
              }],
              content: '',
              role: 'assistant',
              createdAt: new Date().toISOString(),
            };
            messages.push(assistantMessage);
            set(state => ({
              messages: [...state.messages, ...messages],
            }));
          },
          decorateUrl: async (item, defaultUrl?: string) => {
            let url: string | undefined;
            if (typeof decorateUrl === 'function') {
              url = await decorateUrl(item) || undefined;
            }
            return url || defaultUrl || '#';
          },
          // remove a response or a thread from history and StoredHistory
          removeHistory: (history: LlmHistory) => {
            // console.log('removeHistory', history);
            const state = get();
            const mode = state.theme.mode;
            removeItemFromHistory(mode === 'page' ? {
              threadId: history.threadId,
              messageId: history.messageId,
            } : {
              threadId: history.threadId,
            });
            const newHistory = state.history.filter(x => mode === 'page' ? (
              x.messageId !== history.messageId
            ) : (
              x.threadId !== history.threadId
            ));
            set(state => ({
              history: newHistory,
              storedHistory: historyToStoredHistory(newHistory),
            }));
          },
          // retrieve a response or a thread from history
          setHistory: (history: LlmHistory) => {
            // console.log('setHistory', history);
            set(state => ({
              threadId: history.threadId,
              message: history.assistant,
              messages: history.messages,
              /*
              messages: state.messages.findIndex(x => x.messageId === history.messageId) === -1 ?
                [...state.messages, history.user, history.assistant] :
                state.messages,
              */
            }));
          },
          onAction: async (item) => {
            if (typeof onAction === 'function') {
              return await onAction(item);
            }
            return;
          },
          onMessage: async (message) => {
            if (typeof onMessage === 'function') {
              return await onMessage(message);
            }
            return;
          },
          clear: () => {
            const { abort } = get().actions;
            abort();
            set(state => ({
              message: undefined,
              messages: [],
              chunks: undefined,
              threadId: undefined,
              streaming: false,
            }));
          },
        },
      }),
      {
        name: storeKey,
        storage: createJSONStorage(() => props.storageMode === 'session' ? sessionStorage : localStorage),
        onRehydrateStorage: () => () => {
          useStore.setState({ hydrated: true });
        },
        merge: (persistedState: any, currentState: LlmState) => {
          if (persistedState.app) {
            currentState.app = { ...persistedState.app };
          }
          if (persistedState.threadId) {
            currentState.threadId = persistedState.threadId;
          }
          currentState.storedHistory = persistedState.storedHistory && [...persistedState.storedHistory];
          currentState.speakEnabled = persistedState.speakEnabled || false;
          speech.enabled = currentState.speakEnabled;
          /*
          if (persistedState.messages) {
            currentState.messages = [...persistedState.messages];
          }
          */
          currentState.hydrated = true;
          // console.log('currentState', currentState);
          return currentState;
        },
        partialize: (state) => {
          const enabledKeys = state.storageMode === 'session' ?
            ['locale', 'speakEnabled', 'threadId'] :
            ['locale', 'speakEnabled'];
          const store = Object.fromEntries(
            Object.entries(state).filter(([key]) => enabledKeys.includes(key))
          );
          store.storedHistory = historyToStoredHistory(state.history);
          // console.log('history', store.storedHistory);
          return store;
        },
      }
    )
  );
  return useStore;
};

export function messagesToHistory(messages: LlmDecodedMessage[]): LlmHistory[] {
  const history = [...messages].reverse().reduce<LlmHistory[]>((p, c) => {
    let currentItem: Partial<LlmHistory>;
    if (c.role === 'assistant') {
      currentItem = {
        messages,
        threadId: c.threadId,
        messageId: c.messageId,
        createdAt: c.createdAt,
        assistant: {
          ...c,
          text: c.chunks.filter(x => x.type === 'text').map(x => (x as LlmChunkText).text).join(' '),
        },
      };
      p.push(currentItem as LlmHistory);
    } else if (p.length > 0) {
      currentItem = {
        user: {
          ...c,
          text: c.chunks.filter(x => x.type === 'text').map(x => (x as LlmChunkText).text).join(' '),
        },
      };
      Object.assign(p[p.length - 1], currentItem);
    }
    return p;
  }, []);
  // console.log('useLlm.messagesToHistory', history);
  return history.filter(x => x.threadId && x.messageId);
}

export function historyToStoredHistory(history: LlmHistory[]): LlmStoredHistory[] {
  return history.map(x => ({
    threadId: x.threadId,
    messageId: x.messageId,
  }));
}

let sharedLlmStore: LlmStore | undefined;
function createSharedLlmStore(props: LlmOptions & { label: (key: string) => string }): LlmStore {
  if (!sharedLlmStore) {
    sharedLlmStore = createLlmStore(props);
  }
  return sharedLlmStore;
}

export const LlmUseContext = createContext<LlmStore | null>(null);

type LlmProviderProps = React.PropsWithChildren<Omit<LlmOptions, 'locale'>>;

function LlmProvider({ children, ...props }: LlmProviderProps) {
  const label = useLabel();
  if (props.embedded) {
    props.dismissable = false;
    props.opened = true;
  }
  if (props.preview) {
    props.dismissable = false;
    props.opened = true;
    props.skipCustomIntro = true;
  }
  const storeRef = useRef<LlmStore>(undefined);
  if (!storeRef.current) {
    storeRef.current = createSharedLlmStore({ ...props, label });
  }
  return storeRef.current && (
    <LlmUseContext.Provider value={storeRef.current}>
      <LlmViewProvider {...props}>
        {children}
      </LlmViewProvider>
    </LlmUseContext.Provider>
  );
}

function useLlm<T>(selector: (state: LlmState) => T): T {
  const store = useContext(LlmUseContext);
  if (!store) throw new Error('Missing LlmContext.Provider in the tree');
  return useStore(store, selector);
}

export { LlmProvider, useLlm };

